import React, { useCallback, useEffect, useState } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import moment from 'moment';

import modalBuilder from '~/hoc/modalBuilder';
import { getContractsOfPerson, updatePersonInfo } from '~/store/people/actions';
import { getContractDetails } from '~/store/contracts/actions';
import { getLoggedUser } from '~/store/user/actions';
import masked from '~/common/masked';
import InputLabel from '~/components/InputLabel';
import { TableNameTypes, useTracking } from '~/hooks/useTracking';

const { toDate } = masked;

const MODAL_NAME = 'UPDATE_BIRTHDATE';

type UpdateBirthDateProps = {
  birthDate: any;
  personId: number;
  contractId: string;
  closeModal: () => void;
};

function UpdateBirthDate({ birthDate, personId, contractId, closeModal }: UpdateBirthDateProps) {
  const [alreadyTrack, setAlreadyTrack] = useState(false);
  const { handleTracking } = useTracking();

  const contractsControl = useAwaitControl(getContractsOfPerson);
  const getLoggedUserControl = useAwaitControl(getLoggedUser);
  const userData = getLoggedUserControl.result();
  const contractsOfPerson = contractsControl.result();

  const [updatePersonInfoControl, getContractControl] = useAwaitControl([updatePersonInfo, getContractDetails]);

  const updatePersonOk = updatePersonInfoControl.isSuccessful();
  const contractResult = getContractControl.result();

  const onSubmit = useCallback(
    (newBirthDate) => {
      const hasPaidContract = contractsOfPerson?.entries?.some((i: any) => i.contractSituation === 'PAID');
      handleTracking(
        {
          event_name: 'concluiu_editar_dados_contrato_data_nascimento',
          titulo_tela: 'ContractsDetails',
          cliente_person_id: contractResult?.person?.id,
          cliente_convenio: contractResult?.person?.agreementName,
          cliente_oportunidade: contractResult?.person?.isOpportunity ?? false,
          cliente_contrato_pago: hasPaidContract,
          header_antes: JSON.stringify({ birthDate: toDate(birthDate) }),
          header_depois: JSON.stringify({ birthDate: moment(newBirthDate, 'L', 'pt-br').format('DD/MM/YYYY') }),
          cliente_user_id: userData?.id,
          cliente_integrado: null,
        },
        TableNameTypes.BACKOFFICE_PERSONAL_DATA,
      );
      updatePersonInfoControl.start({
        birthDate: moment(newBirthDate, 'L', 'pt-br').toDate(),
        id: personId,
      });
    },
    [contractsOfPerson, contractResult, userData, birthDate, personId],
  );

  const onValueChange = () => {
    if (alreadyTrack) return;
    const hasPaidContract = contractsOfPerson?.entries?.some((i: any) => i.contractSituation === 'PAID');
    handleTracking(
      {
        event_name: 'iniciou_editar_dados_contrato_data_nascimento',
        titulo_tela: 'ContractsDetails',
        cliente_person_id: contractResult?.person?.id,
        cliente_convenio: contractResult?.person?.agreementName,
        cliente_oportunidade: contractResult?.person?.isOpportunity ?? false,
        cliente_contrato_pago: hasPaidContract,
        header_antes: null,
        header_depois: null,
        cliente_user_id: userData?.id,
        cliente_integrado: null,
      },
      TableNameTypes.BACKOFFICE_PERSONAL_DATA,
    );
    setAlreadyTrack(true);
  };

  useEffect(() => {
    if (updatePersonOk) {
      closeModal?.();
      getContractControl.start({ id: contractId });
      updatePersonInfoControl.clear();
    }
  }, [updatePersonOk]);

  return (
    <InputLabel
      title="DATA DE NASCIMENTO"
      format="99/99/9999"
      initialValue={toDate(birthDate)}
      onSubmit={onSubmit}
      onValueChange={onValueChange}
    />
  );
}

export default modalBuilder(UpdateBirthDate, MODAL_NAME, {
  title: 'Alterar data de nascimento',
  centered: true,
});
